import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import {
    Bot, GitBranch, ArrowRightLeft, Globe, Webhook, Users, Plug,
    CheckCircle2, XCircle, Clock, AlertCircle, Loader2, MinusCircle, FileText,
} from "lucide-react";

const TYPE_VISUAL = {
    agent: { icon: Bot, color: "#7C3AED", label: "Agent" },
    condition: { icon: GitBranch, color: "#F59E0B", label: "If / Then" },
    transform: { icon: ArrowRightLeft, color: "#10B981", label: "Transform" },
    webhook: { icon: Webhook, color: "#06B6D4", label: "Webhook" },
    api: { icon: Globe, color: "#3B82F6", label: "API" },
    api_call: { icon: Plug, color: "#8B5CF6", label: "API Call" },
    manual_approval: { icon: Users, color: "#EC4899", label: "Approval" },
    protocol_intel: { icon: FileText, color: "#A78BFA", label: "Protocol Intel" },
};

const STATUS_VISUAL = {
    pending: { icon: Clock, c: "text-white/30" },
    running: { icon: Loader2, c: "text-amber-400 animate-spin" },
    completed: { icon: CheckCircle2, c: "text-emerald-400" },
    failed: { icon: XCircle, c: "text-red-400" },
    skipped: { icon: MinusCircle, c: "text-white/30" },
    awaiting_approval: { icon: Users, c: "text-amber-400" },
    error: { icon: AlertCircle, c: "text-red-400" },
};

function FlowNodeCard({ data, selected }) {
    const visual = TYPE_VISUAL[data.type] || TYPE_VISUAL.transform;
    const Icon = visual.icon;
    const status = data.execStatus;  // injected during run for live overlay
    const StatusIcon = status ? STATUS_VISUAL[status]?.icon : null;
    const StatusClass = status ? STATUS_VISUAL[status]?.c : "";

    const isParallel = !!data.parallel_group;
    const hasError = data.execError;

    return (
        <div
            data-testid={`flow-node-${data.id}`}
            className={`group relative bg-[#0a0a0a] border rounded-md min-w-[220px] max-w-[280px] transition-all ${
                selected
                    ? "border-[#7C3AED] shadow-[0_0_0_2px_rgba(124,58,237,0.25)]"
                    : status === "running"
                    ? "border-amber-400/60 shadow-[0_0_0_2px_rgba(251,191,36,0.18)]"
                    : status === "failed"
                    ? "border-red-400/40"
                    : status === "completed"
                    ? "border-emerald-400/40"
                    : "border-white/15 hover:border-white/30"
            }`}
        >
            <Handle
                type="target"
                position={Position.Top}
                className="!w-2 !h-2 !-top-1"
                isConnectable={!data.readonly}
            />
            <div className="px-3 py-2.5">
                <div className="flex items-center gap-2 mb-1">
                    <div
                        className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${visual.color}20`, color: visual.color }}
                    >
                        <Icon className="w-3 h-3" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-white truncate">{data.name}</div>
                        <div className="font-mono text-[9px] text-white/40 truncate">
                            {visual.label} · {data.id.slice(0, 8)}
                        </div>
                    </div>
                    {StatusIcon && (
                        <div className="shrink-0" data-testid={`flow-node-status-${data.id}`} data-status={status} aria-label={`status ${status}`}>
                            <StatusIcon className={`w-3.5 h-3.5 ${StatusClass}`} />
                        </div>
                    )}
                </div>
                {(isParallel || data.condition || data.timeout_seconds || (data.retry?.max > 1)) && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {isParallel && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 font-mono">
                                ∥ {data.parallel_group}
                            </span>
                        )}
                        {data.condition && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400/10 border border-amber-400/30 text-amber-400">
                                if
                            </span>
                        )}
                        {data.retry?.max > 1 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/15 text-white/60 font-mono">
                                ↺{data.retry.max}
                            </span>
                        )}
                        {data.timeout_seconds && data.timeout_seconds !== 120 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/15 text-white/60 font-mono">
                                {data.timeout_seconds}s
                            </span>
                        )}
                    </div>
                )}
                {data.execLatency != null && (
                    <div className="font-mono text-[9px] text-white/40 mt-1.5">
                        {data.execLatency} ms
                        {data.execCost ? ` · $${data.execCost.toFixed(5)}` : ""}
                    </div>
                )}
                {hasError && (
                    <div className="mt-1.5 text-[9px] text-red-400 truncate">{data.execError}</div>
                )}
            </div>
            <Handle
                type="source"
                position={Position.Bottom}
                className="!w-2 !h-2 !-bottom-1"
                isConnectable={!data.readonly}
            />
        </div>
    );
}

export default memo(FlowNodeCard);

export { TYPE_VISUAL };
